/**
 * Voxly — Media permission helper.
 *
 * Wraps `navigator.mediaDevices.getUserMedia` with:
 *  - Pre-check using `navigator.permissions.query({name: 'camera'})` if available
 *  - Friendly, platform-aware error messages
 *  - Detection of insecure context, missing API, iframe sandboxing
 *
 * Returns a normalized result the caller can render directly:
 *    { ok: true,  stream }              — granted
 *    { ok: false, code, title, message, howTo } — denied / blocked
 *
 * Codes:
 *   "denied"       — user said No
 *   "dismissed"    — user closed the prompt
 *   "in-use"       — another tab/app holds the device
 *   "not-found"    — no camera/mic on the device
 *   "insecure"     — page not HTTPS
 *   "iframe"       — running in an iframe without `allow="camera; microphone"`
 *   "unsupported"  — browser too old
 *   "unknown"      — fallback
 */

const isiOS = () => /iPad|iPhone|iPod/i.test(navigator.userAgent);
const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);

const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const isSecure = () => {
  if (typeof window === "undefined") return true;
  return (
    window.isSecureContext === true ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
};

const howToBy = (kind, codeName) => {
  // kind: 'camera' | 'microphone' | 'both'
  const which = kind === "camera" ? "camera" : kind === "microphone" ? "microphone" : "camera and microphone";
  if (codeName === "iframe") {
    return [
      `This page is embedded in another page that hasn't allowed ${which}.`,
      `Open Voxly directly in your browser tab to enable it, or contact the site owner to add allow="${kind === "both" ? "camera; microphone" : kind}" to the iframe.`,
    ];
  }
  if (codeName === "insecure") {
    return [
      `Your browser blocks ${which} on insecure connections.`,
      `Reload Voxly using https:// (the lock icon should be visible in the address bar).`,
    ];
  }
  if (isiOS()) {
    return [
      `iPhone Settings → Safari → ${kind === "camera" ? "Camera" : "Microphone"} → Allow.`,
      `Then reload this page.`,
      `Tip: in Safari, also tap the "AA" icon in the URL bar → Website Settings → ${kind === "camera" ? "Camera" : "Microphone"} → Allow.`,
    ];
  }
  if (isMobile()) {
    return [
      `Tap the lock 🔒 icon next to the address bar.`,
      `Set ${which} to Allow.`,
      `Reload this page.`,
    ];
  }
  return [
    `Click the lock 🔒 icon in the address bar.`,
    `Set ${which} to Allow (it might say "Blocked").`,
    `Reload this page so Voxly can request access again.`,
  ];
};

export async function requestMedia({ video = false, audio = true, facingMode } = {}) {
  const kind = video && audio ? "both" : video ? "camera" : "microphone";

  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      code: "unsupported",
      title: "Browser too old",
      message: `Your browser doesn't support live ${kind === "both" ? "camera & microphone" : kind} capture.`,
      howTo: ["Update your browser to the latest version of Chrome, Safari, or Firefox."],
    };
  }

  if (!isSecure()) {
    return {
      ok: false,
      code: "insecure",
      title: "Voxly needs HTTPS",
      message: "Your browser blocks camera and microphone on insecure pages.",
      howTo: howToBy(kind, "insecure"),
    };
  }

  // Pre-check via Permissions API where available — gives clearer messaging.
  // Note: not all browsers (notably Firefox/Safari) support 'camera'/'microphone' in permissions.query.
  if (navigator.permissions?.query) {
    try {
      const results = [];
      if (video) results.push(await navigator.permissions.query({ name: "camera" }));
      if (audio) results.push(await navigator.permissions.query({ name: "microphone" }));
      const denied = results.find((r) => r.state === "denied");
      if (denied) {
        return {
          ok: false,
          code: "denied",
          title: `${kind === "both" ? "Camera & microphone" : kind === "camera" ? "Camera" : "Microphone"} blocked`,
          message: `${denied === results[0] && video ? "Camera" : "Microphone"} access is blocked for Voxly.`,
          howTo: howToBy(kind, "denied"),
        };
      }
    } catch {
      /* permissions.query unsupported — fall through to getUserMedia */
    }
  }

  try {
    // Honour the caller's `facingMode` (e.g. "user" for selfie, "environment"
    // for rear). Without this, the camera-flip button in CreatePostPage was
    // a no-op because we'd always hand back the same front-camera track.
    const videoConstraints = video
      ? (facingMode ? { facingMode: { ideal: facingMode } } : true)
      : false;
    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio,
    });
    return { ok: true, stream };
  } catch (e) {
    const name = e?.name || "Error";
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      const code = inIframe() ? "iframe" : "denied";
      return {
        ok: false,
        code,
        title: `${kind === "both" ? "Camera & microphone" : kind === "camera" ? "Camera" : "Microphone"} access denied`,
        message:
          code === "iframe"
            ? "Voxly is running inside an iframe that doesn't allow camera/microphone."
            : "You denied access. Voxly can't capture without permission.",
        howTo: howToBy(kind, code),
      };
    }
    if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
      return {
        ok: false,
        code: "not-found",
        title: `No ${kind === "both" ? "camera or microphone" : kind} found`,
        message: "Your device doesn't have the hardware Voxly needs, or it's disabled.",
        howTo: ["Check your device settings to confirm the hardware is enabled."],
      };
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
      return {
        ok: false,
        code: "in-use",
        title: `${kind === "both" ? "Camera/microphone" : kind === "camera" ? "Camera" : "Microphone"} busy`,
        message: "Another app or tab is using your hardware.",
        howTo: [
          "Close other tabs that might be using video conferencing or recording.",
          "Quit Zoom, Meet, FaceTime, OBS, etc., then try again.",
        ],
      };
    }
    if (name === "AbortError") {
      return {
        ok: false,
        code: "dismissed",
        title: "Request cancelled",
        message: "The browser dismissed the request before it could complete.",
        howTo: ["Tap the action again to retry."],
      };
    }
    return {
      ok: false,
      code: "unknown",
      title: "Couldn't access device",
      message: `${name}: ${e?.message || "Unexpected error"}`,
      howTo: ["Reload the page and try again."],
    };
  }
}
